import React from 'react'
import { Button, Card, CardContent, Checkbox, Label, Switch } from '@/shared/ui'
import { listWorkspaceChannels, saveSelectedChannels, setWorkspaceEnabled, SlackChannel, SlackWorkspace } from '../services/slackService'

type Props = { workspace: SlackWorkspace; onToggleEnabled?: (workspaceId: number, enabled: boolean) => void }

export default function WorkspaceCard({ workspace, onToggleEnabled }: Props) {
  const [channels, setChannels] = React.useState<SlackChannel[]>([])
  const [loading, setLoading] = React.useState(false)
  const [saved, setSaved] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [enabled, setEnabled] = React.useState<boolean>(!!workspace.enabled)
  const [toggling, setToggling] = React.useState<boolean>(false)

  React.useEffect(() => { setEnabled(!!workspace.enabled) }, [workspace.enabled])

  async function syncChannels() {
    setLoading(true)
    setError(null)
    setSaved(false)
    try {
      const list = await listWorkspaceChannels(workspace.id)
      setChannels(list)
    } catch (e:any) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  function toggle(id: string) {
    setChannels(prev => prev.map(c => c.id === id ? { ...c, selected: !c.selected } : c))
  }

  async function onSave() {
    setLoading(true)
    setError(null)
    setSaved(false)
    try {
      const payload = channels.map(c => ({ id: c.id, name: c.name, notify: !!c.selected }))
      await saveSelectedChannels(workspace.id, payload)
      setSaved(true)
    } catch (e:any) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card>
      <CardContent className="pt-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-base font-medium">{workspace.team_name}</div>
            <div className="text-xs text-foreground/60">ID: {workspace.team_id}</div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Label className="text-xs text-foreground/70">Notificações</Label>
              <Switch checked={enabled} onChange={async (e:any) => {
                const next = !!(e?.target?.checked)
                setToggling(true)
                setError(null)
                try {
                  await setWorkspaceEnabled(workspace.id, next)
                  setEnabled(next)
                  onToggleEnabled?.(workspace.id, next)
                } catch (err:any) {
                  setError(err.message || String(err))
                } finally {
                  setToggling(false)
                }
              }} disabled={toggling} />
            </div>
            <div className="space-x-2">
              <Button onClick={syncChannels} disabled={loading}>Sincronizar canais</Button>
              <Button onClick={onSave} disabled={loading || channels.length===0}>Salvar seleção</Button>
              {saved && <span className="text-sm text-green-600">Salvo.</span>}
            </div>
          </div>
        </div>
        {error && <div className="text-sm text-red-500">{error}</div>}
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
          {channels.map(ch => (
            <label key={ch.id} className="flex items-center gap-2 rounded-md border border-border bg-muted/30 p-2">
              <Checkbox checked={!!ch.selected} onChange={()=> toggle(ch.id)} />
              <span className="text-sm">#{ch.name}</span>
            </label>
          ))}
          {channels.length === 0 && (
            <div className="text-sm text-foreground/60">Nenhum canal carregado. Clique em "Sincronizar canais".</div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

